import { NgIf } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, OnInit } from "@angular/core";
import { flatMapSuperDeep } from "@ankaadia/ankaadia-shared";
import { FieldWrapper, FormlyFieldConfig, FormlyModule } from "@ngx-formly/core";
import { compact, isEmpty } from "lodash";
import { PrimeTemplate } from "primeng-v17/api";
import { FieldsetModule } from "primeng-v17/fieldset";
import { TooltipModule } from "primeng-v17/tooltip";
import { debounceTime, distinctUntilChanged, map, merge, startWith } from "rxjs";
import { FormElementMapModule } from "../../shared/from-element-map/form-element-map.module";
import { outsideZone } from "../../shared/rxjs/outside-zone";

@Component({
  selector: "app-formly-field-set",
  templateUrl: "./formly-field-set.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FieldsetModule, PrimeTemplate, NgIf, TooltipModule, FormElementMapModule, FormlyModule],
})
export class FormlyFieldSetComponent extends FieldWrapper implements OnInit {
  isInvalid = false;
  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly ngZone: NgZone
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.props.showError) {
      this.setupInvalidityChange();
    }
  }

  private setupInvalidityChange(): void {
    merge(this.options.fieldChanges, this.form.statusChanges)
      .pipe(
        startWith(undefined),
        debounceTime(100),
        map(() => this.calculateInvalidity()),
        distinctUntilChanged(),
        outsideZone(this.ngZone)
      )
      .subscribe((invalidity) => this.setInvalidity(invalidity));
  }

  private calculateInvalidity(): boolean {
    const isInvalid = (field: FormlyFieldConfig): boolean =>
      field.fieldGroup?.length && !field.fieldArray
        ? field.fieldGroup.some((f) => isInvalid(f))
        : field.formControl.invalid && (!!field.formControl.errors || this.hasNestedErrors(field));
    return this.field.fieldGroup.some((field) => isInvalid(field));
  }

  private setInvalidity(invalidity: boolean): void {
    this.isInvalid = invalidity;
    this.changeDetector.detectChanges();
  }

  // errors are not always propagated to the parent field, hence we also check the nested fields for errors
  private hasNestedErrors(field: FormlyFieldConfig): boolean {
    const nestedErrors = flatMapSuperDeep(field?.fieldGroup, (x) => x?.fieldGroup).map((x) => x?.formControl?.errors);
    return !isEmpty(compact(nestedErrors));
  }
}
